import React, { useEffect, useRef, useState } from "react";
import InstallPWA from "./InstallPWA";
import CampsitePicker from "./CampsitePicker";
import DevProToggle from "./DevProToggle";
import { LogOut } from "lucide-react";
import { getSeasonForDate } from "../lib/scoring";
import { trackEvent } from "../lib/analytics";
import { getHomepageHeroVariant, getHomepageHeroCopyKeys } from "../config/homepageHero";

/**
 * Toolbar
 * - Campsite selector
 * - My location button
 * - Install PWA button
 * - Units toggle
 * - Theme toggle
 *
 * Pure UI component: no localStorage, no side effects.
 */
// Shared by units/language/theme/PWA/logout so the settings row is uniform.
// Logout keeps its text tokens (text-slate-600 / dark:text-slate-300) on every control.
const SETTINGS_CONTROL_CLASS =
  "inline-flex min-h-[44px] items-center justify-center gap-1 whitespace-nowrap px-2 py-1 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300 dark:hover:text-slate-100 dark:hover:bg-slate-800 focus-ring cursor-pointer transition-colors";

export default function Toolbar({
  t,
  lang,
  onToggleLanguage,
  siteList,
  siteId,
  onSelectSite,
  onUseMyLocation,
  units,
  onToggleUnits,
  darkMode,
  onToggleTheme,

  // NEW: dev pro toggle plumbing
  devPro,
  onToggleDevPro,

  // Logout row: shown only for a signed-in user (existence, not tier)
  isSignedIn = false,
  onLogout,
  loggingOut = false,
}) {
  // NOTE (Ticket 411, #411): two independent "winter" concepts coexist in
  // this component. `season` below is scoring.js's browser-local,
  // October-April "winter hint/badge" rule (unchanged) — it only controls
  // the small ❄ hint under the hero, not the hero copy itself. `heroVariant`
  // is a separate, Reykjavik-calendar-based presentation choice (see
  // src/config/homepageHero.js) that controls the hero title/subtitle/CTA
  // text. Their boundaries do not match (Oct 1 vs Sept 1, Apr 30 vs Mar 31)
  // and are not meant to — keep them independent, do not unify them.
  const season = getSeasonForDate(new Date());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsOpenRef = useRef(false);
  const settingsToggleRef = useRef(null);

  useEffect(() => {
    settingsOpenRef.current = settingsOpen;
  }, [settingsOpen]);

  // Close the panel only after a successful logout. Focus returns to the
  // Settings toggle only if the panel was still open when the request settled,
  // so a user who closed it in the meantime is not pulled back to it.
  async function handleLogout() {
    if (!onLogout || loggingOut) return;
    const ok = await onLogout();
    if (ok && settingsOpenRef.current) {
      setSettingsOpen(false);
      settingsToggleRef.current?.focus();
    }
  }

  const heroVariant = getHomepageHeroVariant(new Date());
  const heroCopyKeys = getHomepageHeroCopyKeys(heroVariant);
  const heroCtaLabel = t?.(heroCopyKeys.ctaKey) ?? "Finna betri stað";

  function scrollToComparison() {
    document.getElementById("comparison-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    trackEvent("homepage_hero_cta_click");
    trackEvent("homepage_primary_cta_clicked", {
      hero_variant: heroVariant,
      cta_label: heroCtaLabel,
      language: lang,
    });
  }

  return (
    <header className="relative z-30 mb-3 md:mb-4">
      {/* Row 1: Hero copy (left) + campsite selector (right) */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        {/* Hero copy */}
        <div className="max-w-xl">
          <h1 className="text-2xl md:text-3xl font-black tracking-tight leading-tight">
            {t?.(heroCopyKeys.titleKey)}
          </h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-md leading-relaxed">
            {t?.(heroCopyKeys.subtitleKey)}
          </p>

          <button
            type="button"
            onClick={scrollToComparison}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-emerald-700 active:bg-emerald-800 transition-colors"
          >
            {heroCtaLabel} →
          </button>

          {season === "winter" && (
            <div className="mt-3 space-y-1">
              <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                <span>{t("weatherNearbyHint")}</span>
                <span className="cursor-pointer" title={t("weatherNearbyTooltip")}>ⓘ</span>
              </div>
              <div
                title={t("winterModeTooltip")}
                className="inline-flex items-center gap-1 text-xs font-medium text-sky-300 dark:text-sky-400"
              >
                <span>❄</span>
                <span>{t("winterModeActive")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Primary selector + settings toggle — grouped in right column */}
        <div className="relative z-30 flex flex-col items-end gap-1 self-start">
          <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200/80 bg-white/70 px-3 py-2.5 shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-900/70">
            <label htmlFor="site" className="sr-only">Campsite</label>
            <CampsitePicker siteList={siteList} siteId={siteId} onSelectSite={onSelectSite} t={t} />
            <button
              onClick={onUseMyLocation}
              className="px-3 py-2 rounded-xl border border-slate-300 bg-white shadow-sm focus-ring smooth text-slate-900 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100 inline-flex items-center gap-2 text-sm whitespace-nowrap"
              aria-label="Use my current location"
              title="Use my current location"
            >
              <span>📍</span>
              <span>{t?.("myLocation")}</span>
            </button>
          </div>

          <button
            ref={settingsToggleRef}
            type="button"
            aria-expanded={settingsOpen}
            aria-controls="toolbar-settings-panel"
            onClick={() => setSettingsOpen((v) => !v)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800 transition-colors"
          >
            <span aria-hidden>⚙</span>
            <span>{t?.("settingsLabel")}</span>
            <span aria-hidden className="opacity-50 text-[10px]">{settingsOpen ? "▲" : "▼"}</span>
          </button>

          {settingsOpen && (
            <div
              id="toolbar-settings-panel"
              className="flex flex-wrap items-center justify-end gap-1.5 rounded-xl border border-slate-200/80 bg-slate-50/80 px-3 py-2 shadow-sm dark:border-slate-700/60 dark:bg-slate-800/40"
            >
              <InstallPWA className={SETTINGS_CONTROL_CLASS} />

              <button
                type="button"
                onClick={onToggleUnits}
                className={SETTINGS_CONTROL_CLASS}
                aria-label={units === "metric" ? "Switch to imperial units" : "Switch to metric units"}
                title={units === "metric" ? "Metric units: °C, mm, m/s" : "Imperial units: °F, in, knots"}
              >
                {units === "metric" ? "°C" : "°F"}
              </button>

              <button
                type="button"
                onClick={onToggleLanguage}
                className={SETTINGS_CONTROL_CLASS}
                title={t?.("toolbar.toggleLanguage") ?? "Toggle language"}
              >
                🌐 {lang === "is" ? "EN" : "IS"}
              </button>

              <button
                type="button"
                onClick={onToggleTheme}
                className={SETTINGS_CONTROL_CLASS}
                aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                title="Toggle dark mode"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>

              {import.meta.env.DEV && <DevProToggle devPro={devPro} onToggleDevPro={onToggleDevPro} />}

              {isSignedIn && (
                // Content-sized group owns the divider, so the bar always sits beside its button.
                <div className="flex shrink-0 items-center border-l border-slate-200/80 pl-2 ml-1 dark:border-slate-700/60">
                  <button
                    type="button"
                    onClick={handleLogout}
                    disabled={loggingOut}
                    aria-busy={loggingOut}
                    className={`${SETTINGS_CONTROL_CLASS} disabled:opacity-60`}
                  >
                    <LogOut size={16} aria-hidden="true" focusable="false" />
                    <span>{loggingOut ? t?.("loggingOutLabel") : t?.("logoutLabel")}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
